import React, { useState, useEffect } from 'react';
import { accountingAPI, formatCurrency } from '../services/api';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Download } from 'lucide-react';
import api from '../services/api';

export default function TrialBalance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date_from: '', date_to: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await accountingAPI.trialBalance(params);
      setData(res.data);
    } catch { toast.error('Failed to load trial balance'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filters.date_from, filters.date_to]);

  const TYPE_ORDER = ['asset', 'liability', 'equity', 'revenue', 'direct_expense', 'indirect_expense'];
  const TYPE_LABELS = { asset: 'Assets', liability: 'Liabilities', equity: 'Equity', revenue: 'Revenue', direct_expense: 'Direct Expenses', indirect_expense: 'Indirect Expenses' };

  const grouped = {};
  data?.accounts?.forEach(a => {
    const type = a.account_type || 'other';
    if (!grouped[type]) grouped[type] = [];
    grouped[type].push(a);
  });

  return (
    <div data-testid="trial-balance-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Trial Balance</h1>
          <p className="text-sm text-slate-500 mt-1">Verify all accounts balance</p>
        </div>
        <div className="flex gap-3 items-center">
          <input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
            className="form-input w-36" data-testid="tb-date-from" />
          <input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
            className="form-input w-36" data-testid="tb-date-to" />
          {data && (
            data.is_balanced ? (
              <span className="flex items-center gap-1 text-xs text-emerald-600 font-semibold bg-emerald-50 px-3 py-1.5 rounded-full">
                <CheckCircle className="w-4 h-4" /> Balanced
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-rose-600 font-semibold bg-rose-50 px-3 py-1.5 rounded-full">
                <XCircle className="w-4 h-4" /> Unbalanced
              </span>
            )
          )}
          <button onClick={async () => {
            try {
              const params = new URLSearchParams({ format: 'csv' });
              if (filters.date_from) params.set('date_from', filters.date_from);
              if (filters.date_to) params.set('date_to', filters.date_to);
              const res = await api.get(`/accounting/trial-balance/export?${params}`, { responseType: 'blob' });
              const url = URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a'); a.href = url; a.download = 'trial_balance.csv'; a.click();
              toast.success('Trial Balance exported');
            } catch { toast.error('Export failed'); }
          }} className="btn-accent flex items-center gap-1.5 text-xs px-3" data-testid="tb-export">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full" data-testid="tb-table">
            <thead>
              <tr className="bg-slate-900 text-white">
                <th className="px-4 py-3 text-left text-xs font-bold uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase">Account Name</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase">Total Debit</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase">Total Credit</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase">Closing Debit</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase">Closing Credit</th>
              </tr>
            </thead>
            <tbody>
              {TYPE_ORDER.map(type => {
                const accs = grouped[type];
                if (!accs || accs.length === 0) return null;
                return (
                  <React.Fragment key={type}>
                    <tr className="bg-slate-50">
                      <td colSpan={6} className="px-4 py-2 font-semibold text-sm text-slate-700">{TYPE_LABELS[type] || type}</td>
                    </tr>
                    {accs.map(a => (
                      <tr key={a.account_code} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tb-row-${a.account_code}`}>
                        <td className="px-4 py-2 font-mono text-xs text-slate-500">{a.account_code}</td>
                        <td className="px-4 py-2 text-sm text-slate-700 pl-8">{a.account_name}</td>
                        <td className="px-4 py-2 text-right font-mono text-sm">{a.total_debit > 0 ? formatCurrency(a.total_debit) : '-'}</td>
                        <td className="px-4 py-2 text-right font-mono text-sm">{a.total_credit > 0 ? formatCurrency(a.total_credit) : '-'}</td>
                        <td className="px-4 py-2 text-right font-mono text-sm text-blue-700">{a.closing_debit > 0 ? formatCurrency(a.closing_debit) : '-'}</td>
                        <td className="px-4 py-2 text-right font-mono text-sm text-rose-700">{a.closing_credit > 0 ? formatCurrency(a.closing_credit) : '-'}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-slate-900 text-white font-bold">
                <td colSpan={2} className="px-4 py-3 text-sm">Grand Total</td>
                <td className="px-4 py-3 text-right font-mono text-sm" colSpan={2}></td>
                <td className="px-4 py-3 text-right font-mono text-sm" data-testid="tb-grand-debit">{formatCurrency(data?.grand_debit || 0)}</td>
                <td className="px-4 py-3 text-right font-mono text-sm" data-testid="tb-grand-credit">{formatCurrency(data?.grand_credit || 0)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
